import { useQuery } from '@apollo/client';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import Button from '../../../components/form/button';
import Field from '../../../components/form/field';
import Input from '../../../components/form/input';
import Select from '../../../components/form/select';
import Modal from '../../../components/modal/default';
import { CLINICS_GET } from '../../../graphql/query/clinics';
import useAuth from '../../../hooks/auth';
import useToaster from '../../../hooks/toast';
import {
  Clinic,
  GraphQLResponseTitan,
  IModalDefault,
  ISelectOption,
  IUserInvite,
} from '../../../types';
import { logger } from '../../../utils/logger';
import styles from './styles.module.css';

const InviteUsers: FC<IModalDefault> = props => {
  const { onClose, ...rest } = props;
  const {
    inviteClinician: inviteClinicianAuth,
    inviteAdmin,
    isLoading: isAuthLoading,
    user: currentUser,
  } = useAuth();
  const [availableClinics, setAvailableClinics] = useState<ISelectOption<Clinic>[]>([]);
  const [error, setError] = useState('');
  const { success, danger } = useToaster();
  const { data: dataClinics } = useQuery<GraphQLResponseTitan<'clinics', Clinic[]>>(CLINICS_GET);
  const userClinics = currentUser?.acl?.clinics || [];
  const [user, setUser] = useState<IUserInvite>({
    email: '',
    role: 0,
    msg: '',
    clinicId: userClinics.length === 1 ? userClinics[0] : '0',
    firstName: '',
    lastName: '',
  });

  // Checking if the user has the right permission because the legacy API
  // requires it and doesn't return which permission is needed.
  const canCreateUser = currentUser?.permissions?.includes('USER_CREATE') || false;
  const isLoading = isAuthLoading;

  // set up clinics for select
  useEffect(() => {
    if (dataClinics?.clinics) {
      // set available clinics for use in form
      setAvailableClinics(
        dataClinics.clinics
          .filter(c => userClinics.map(s => s.toString()).includes(c.id.toString()))
          .map(c => ({
            value: c.id.toString(),
            label: c.displayName,
            data: c,
          }))
      );
    }
    if (userClinics.length === 1) {
      setUser({
        email: '',
        role: 0,
        msg: '',
        clinicId: userClinics[0],
        firstName: '',
        lastName: '',
      });
    }
  }, [dataClinics, userClinics]);

  const handleSetEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setUser(current => ({
      ...current,
      email: e.target.value,
    }));
  };

  const handleSetFirstName = (e: ChangeEvent<HTMLInputElement>) => {
    setUser(current => ({
      ...current,
      firstName: e.target.value,
    }));
  };

  const handleSetLastName = (e: ChangeEvent<HTMLInputElement>) => {
    setUser(current => ({
      ...current,
      lastName: e.target.value,
    }));
  };

  const handleSetRole = (e: ChangeEvent<HTMLSelectElement>) => {
    setUser(current => ({
      ...current,
      role: Number(e.target.value),
    }));
  };

  const handleSetClinic = (e: ChangeEvent<HTMLSelectElement>) => {
    setUser(current => ({
      ...current,
      clinicId: e.target.value,
    }));
  };

  const handleClose = () => {
    // reset state since this component stays around
    setUser({ email: '', role: 0, msg: '', clinicId: '', firstName: '', lastName: '' });
    setError('');
    onClose();
  };

  const handleAdminInvite = async (
    clinicAdmin: Pick<IUserInvite, 'email' | 'role' | 'firstName' | 'lastName' | 'clinicId'>
  ) => {
    setError('');

    try {
      await inviteAdmin(clinicAdmin);
      success('Clinic Admin invited successfully.');
    } catch (err: any) {
      danger('An error occurred trying to invite Clinic Admin user. Please try again.');
      setError('An error occurred trying to invite the user. Please try again.');
      err.message = `Unknown error inviting user: ${err.message}`;
      logger.error(err);
    }
  };

  const handleRegularInvite = async (
    input: Pick<IUserInvite, 'email' | 'clinicId' | 'firstName' | 'lastName'>
  ) => {
    try {
      await inviteClinicianAuth(input);
      success('Clinician invited successfully.');

      setUser(prev => {
        if (prev.role === 5) {
          return { ...prev, email: '', msg: '', firstName: '', lastName: '' };
        }
        return prev;
      });
    } catch (e) {
      danger('An error occurred trying to invite Clinician user. Please try again.');
      setError('An error occurred trying to invite the user. Please try again.');
    }
  };

  const handleInvite = async () => {
    if (!user.email) {
      danger('Email is required to send an invite.');
      return;
    }

    const isClinicianRole = user.role === 5;

    if (isClinicianRole) {
      await handleRegularInvite({
        email: user.email,
        clinicId: user.clinicId,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    } else {
      await handleAdminInvite({
        email: user.email,
        role: user.role,
        firstName: user.firstName,
        lastName: user.lastName,
        clinicId: user.clinicId,
      });
    }
  };

  return (
    <Modal
      {...rest}
      onClose={handleClose}
      title="Invite new users"
      contentProps={{
        style: {
          padding: '1.5rem 2.5rem 1.5rem 2.5rem',
          flexDirection: 'column',
        },
      }}
      style={{
        content: {
          width: '80%',
          maxWidth: '803px',
          maxHeight: '90%',
          overflowY: user ? 'scroll' : 'hidden',
        },
      }}>
      {Boolean(error) && <Field>{error}</Field>}

      <div className={styles.errorWrapper}>
        <div className={styles.fieldWrapper}>
          <Input.Field
            label="Email Address"
            value={user?.email}
            onChange={handleSetEmail}
            fullwidth
            style={{ width: '100%' }}
          />

          <Field
            label="User Type"
            fullwidth
            style={{
              marginLeft: '1.5rem',
            }}>
            <Select.Component
              options={[
                {
                  value: '0',
                  label: 'Select one..',
                },
                ...(canCreateUser
                  ? [
                      {
                        value: '2',
                        label: 'Super Clinic Admin',
                      },
                      {
                        value: '3',
                        label: 'Regular Clinic Admin',
                      },
                    ]
                  : []),
                {
                  value: '5',
                  label: 'Clinician',
                },
              ]}
              style={{ width: '100%' }}
              onChange={handleSetRole}
              value={user?.role}
            />
          </Field>
        </div>
        <div className={styles.fieldWrapper} style={{ gap: '25px' }}>
          <Input.Field
            label="First Name"
            value={user?.firstName}
            onChange={handleSetFirstName}
            fullwidth
            style={{ width: '100%' }}
          />

          <Input.Field
            label="Last Name"
            value={user?.lastName}
            onChange={handleSetLastName}
            fullwidth
            style={{ width: '100%' }}
          />
        </div>
        <div className={styles.fieldWrapper}>
          <Field label="Clinic" style={{ width: '100%' }}>
            <Select.Component
              options={[
                {
                  value: '0',
                  label: 'Select one..',
                },
                ...availableClinics,
              ]}
              style={{ width: '100%' }}
              disabled={userClinics.length === 1}
              onChange={handleSetClinic}
              value={user?.clinicId}
            />
          </Field>
        </div>
        {Boolean(user?.msg) && <span className={styles.error}>{user?.msg}</span>}
      </div>

      <div className="buttons">
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>

        <Button
          onClick={handleInvite}
          variant="dark"
          isLoading={isLoading}
          disabled={isLoading || user.email === '' || user.clinicId === '' || user.role === 0}>
          Invite
        </Button>
      </div>
    </Modal>
  );
};

export default InviteUsers;
